import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

/* ── Constants ── */
const BUCKET = 'images';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];

/**
 * Validate a file before upload.
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateFile(file) {
  if (!file) return { valid: false, error: 'No file selected' };
  if (!ALLOWED_TYPES.includes(file.type)) {
    return { valid: false, error: `Unsupported format. Allowed: JPG, PNG, WebP, GIF, SVG` };
  }
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: `File too large. Max size is 5 MB` };
  }
  return { valid: true };
}

/**
 * Build a storage path for a product image.
 */
function buildPath(file, productId) {
  const ext = file.name.split('.').pop().toLowerCase();
  const ts = Date.now();
  const safe = file.name.replace(/[^a-z0-9.-]/gi, '_').replace(/\.[^.]+$/, '');
  if (productId) return `products/${productId}/${safe}-${ts}.${ext}`;
  return `uploads/${safe}-${ts}.${ext}`;
}

/**
 * Get image dimensions from a File.
 */
function getImageDimensions(file) {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) return resolve({ width: null, height: null });
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(img.src);
    };
    img.onerror = () => resolve({ width: null, height: null });
    img.src = URL.createObjectURL(file);
  });
}

/* ═══════════════════════════════════════════════
 * QUERIES
 * ═══════════════════════════════════════════════ */

/**
 * Fetch all media files (non-deleted), ordered by most recent.
 */
export function useMediaFiles(search = '') {
  return useQuery({
    queryKey: ['media', 'files', search],
    queryFn: async () => {
      let query = supabase
        .from('media_files')
        .select('*, products:linked_product_id(id, title)')
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (search) {
        query = query.or(
          `file_name.ilike.%${search}%,alt_text.ilike.%${search}%,file_path.ilike.%${search}%`
        );
      }
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 30,
  });
}

/**
 * Fetch storage usage stats.
 */
export function useMediaStats() {
  return useQuery({
    queryKey: ['media', 'stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('media_files')
        .select('file_size, mime_type')
        .is('deleted_at', null);
      if (error) throw error;
      const totalSize = (data || []).reduce((s, f) => s + (f.file_size || 0), 0);
      const totalCount = data?.length || 0;
      const byType = {};
      (data || []).forEach((f) => {
        const t = f.mime_type || 'unknown';
        byType[t] = (byType[t] || 0) + 1;
      });
      return { totalSize, totalCount, byType };
    },
    staleTime: 1000 * 60,
  });
}

/* ═══════════════════════════════════════════════
 * MUTATIONS
 * ═══════════════════════════════════════════════ */

/**
 * Upload a file to Supabase Storage and track it in media_files.
 * Returns { path, url, record }.
 *
 * @param {{ file: File, productId?: string, altText?: string, onProgress?: (pct: number) => void }} opts
 */
export function useUploadMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ file, productId, altText, onProgress }) => {
      // 1. Validate
      const check = validateFile(file);
      if (!check.valid) throw new Error(check.error);

      // 2. Check for corrupt / empty images
      if (file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
        const dims = await getImageDimensions(file);
        if (!dims.width || !dims.height) throw new Error('Image appears corrupt or unreadable');
      }

      // 3. Build path & upload
      const filePath = buildPath(file, productId);

      // Use XMLHttpRequest for progress tracking
      const url = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/${BUCKET}/${filePath}`;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const uploadResult = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('x-upsert', 'false');
        xhr.setRequestHeader('cache-control', '3600');

        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              onProgress(Math.round((e.loaded / e.total) * 100));
            }
          });
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ success: true });
          } else {
            try {
              const body = JSON.parse(xhr.responseText);
              reject(new Error(body.message || body.error || `Upload failed (${xhr.status})`));
            } catch {
              reject(new Error(`Upload failed (${xhr.status})`));
            }
          }
        };
        xhr.onerror = () => reject(new Error('Network error during upload'));
        xhr.send(file);
      });

      // 4. Get public URL
      const { data: publicData } = supabase.storage.from(BUCKET).getPublicUrl(filePath);
      const publicUrl = publicData?.publicUrl;

      // 5. Get dimensions
      const dims = file.type.startsWith('image/') ? await getImageDimensions(file) : {};

      // 6. Get current user
      const { data: { user } } = await supabase.auth.getUser();

      // 7. Track in media_files
      const { data: record, error: dbErr } = await supabase
        .from('media_files')
        .insert({
          file_name: file.name,
          file_path: filePath,
          bucket: BUCKET,
          mime_type: file.type,
          file_size: file.size,
          width: dims.width || null,
          height: dims.height || null,
          alt_text: altText || null,
          uploaded_by: user?.id || null,
          linked_product_id: productId || null,
        })
        .select()
        .single();

      if (dbErr) throw dbErr;

      return { path: filePath, url: publicUrl, record };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Delete a media file — soft-delete by default (marks for cleanup).
 * If hardDelete is true, also removes from storage.
 */
export function useDeleteMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, filePath, hardDelete = false }) => {
      if (hardDelete) {
        // Remove from storage
        const { error: storageErr } = await supabase.storage.from(BUCKET).remove([filePath]);
        if (storageErr) throw storageErr;
        // Remove DB record
        const { error: dbErr } = await supabase.from('media_files').delete().eq('id', id);
        if (dbErr) throw dbErr;
      } else {
        // Soft-delete — mark for cleanup
        const { error } = await supabase
          .from('media_files')
          .update({
            marked_for_deletion: true,
            deleted_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', id);
        if (error) throw error;
      }
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Replace a media file — uploads new, marks old for cleanup.
 */
export function useReplaceMedia() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ oldRecord, newFile, onProgress }) => {
      const check = validateFile(newFile);
      if (!check.valid) throw new Error(check.error);

      // Upload new file to the same folder structure
      const filePath = buildPath(newFile, oldRecord.linked_product_id);

      const url = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/${BUCKET}/${filePath}`;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.setRequestHeader('x-upsert', 'false');
        xhr.setRequestHeader('cache-control', '3600');
        if (onProgress) {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
          });
        }
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`Upload failed (${xhr.status})`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(newFile);
      });

      // Get dimensions
      const dims = newFile.type.startsWith('image/') ? await getImageDimensions(newFile) : {};

      const { data: { user } } = await supabase.auth.getUser();

      // Mark old record for deletion
      await supabase
        .from('media_files')
        .update({
          marked_for_deletion: true,
          deleted_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', oldRecord.id);

      // Insert new record
      const { data: newRecord, error } = await supabase
        .from('media_files')
        .insert({
          file_name: newFile.name,
          file_path: filePath,
          bucket: BUCKET,
          mime_type: newFile.type,
          file_size: newFile.size,
          width: dims.width || null,
          height: dims.height || null,
          alt_text: oldRecord.alt_text,
          uploaded_by: user?.id || null,
          linked_product_id: oldRecord.linked_product_id,
        })
        .select()
        .single();

      if (error) throw error;

      return { oldPath: oldRecord.file_path, newPath: filePath, record: newRecord };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });
}

/**
 * Link an existing media file to a product.
 */
export function useLinkMediaToProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ mediaId, productId }) => {
      const { error } = await supabase
        .from('media_files')
        .update({
          linked_product_id: productId,
          updated_at: new Date().toISOString(),
        })
        .eq('id', mediaId);
      if (error) throw error;

      // Also set the product's image_path
      const { data: media } = await supabase
        .from('media_files')
        .select('file_path')
        .eq('id', mediaId)
        .single();

      if (media) {
        await supabase
          .from('products')
          .update({ image_path: media.file_path, updated_at: new Date().toISOString() })
          .eq('id', productId);
      }

      return { mediaId, productId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'products'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Update media alt text.
 */
export function useUpdateMediaAlt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, altText }) => {
      const { error } = await supabase
        .from('media_files')
        .update({ alt_text: altText, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media'] });
    },
  });
}

/* ── Helpers ── */
export function formatFileSize(bytes) {
  if (!bytes) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
}

export { MAX_FILE_SIZE, ALLOWED_TYPES, BUCKET };
